import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, fromEvent, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ScrollDirectionService {
    private scrollDirectionSource = new BehaviorSubject<'up' | 'down' | null>(
        null
    );
    scrollDirection$ = this.scrollDirectionSource.asObservable();

    private lastScrollTop = 0;
    private threshold = 10;
    private scrollSub?: Subscription;
    private scrollElement: HTMLElement | null = null;

    constructor(private ngZone: NgZone) {}

    attachToElement(el: HTMLElement): void {
        this.detach();

        this.scrollElement = el;
        this.lastScrollTop = el.scrollTop;

        this.ngZone.runOutsideAngular(() => {
            this.scrollSub = fromEvent(el, 'scroll')
                .pipe(throttleTime(100))
                .subscribe(() => this.handleScroll());
        });
    }

    detach(): void {
        if (this.scrollSub) {
            this.scrollSub.unsubscribe();
            this.scrollSub = undefined;
        }
    }

    private handleScroll(): void {
        if (!this.scrollElement) return;

        const scrollTop = this.scrollElement.scrollTop;
        const scrollDelta = scrollTop - this.lastScrollTop;

        // Ignore tiny or bounce scrolls
        if (Math.abs(scrollDelta) < this.threshold) return;

        const direction = scrollDelta > 0 ? 'down' : 'up';

        // Ignore up-scroll bounce at top
        if (scrollTop <= 0 && direction === 'up') return;

        // Ignore down-scroll bounce at bottom
        const maxScroll =
            this.scrollElement.scrollHeight - this.scrollElement.clientHeight;
        if (scrollTop >= maxScroll && direction === 'down') return;

        // Only emit if direction changes
        if (this.scrollDirectionSource.value !== direction) {
            this.ngZone.run(() => {
                this.scrollDirectionSource.next(direction);
            });
        }

        this.lastScrollTop = scrollTop;
    }
}
